(function () {
  'use strict';
  if (window.MWAudioPlayer) return;
  const instances = new WeakMap();
  const playback = window.MWAudioPlayback;

  class AudioPlayer {
    constructor(options = {}) {
      const explicitTracks = Object.prototype.hasOwnProperty.call(options, 'tracks');
      if (explicitTracks && !Array.isArray(options.tracks)) throw new TypeError('Audio tracks must be an array.');
      this.currentlyPlaying = null;
      this.audioElements = new Map();
      const suppliedRoot = options.root;
      this.mountRoot = suppliedRoot;
      this.root = suppliedRoot
        ? (suppliedRoot.matches('.mw-simple-player') ? suppliedRoot : suppliedRoot.querySelector('.mw-simple-player'))
        : null;
      this.tracks = [];
      if (!this.root || !explicitTracks) return;
      if (instances.has(this.root)) return instances.get(this.root);
      this.document = this.root.ownerDocument;
      this.tracks = options.tracks.slice();
      this.listeners = [];
      this.playToken = null;
      this.pendingIndex = null;
      this.destroyed = false;
      instances.set(this.root, this);
      this.init();
      this.setupLifecycle();
    }

    listen(target, event, listener) {
      target.addEventListener(event, listener);
      this.listeners.push(() => target.removeEventListener(event, listener));
    }

    setupLifecycle() {
      this.listen(this.document, 'shopify:section:unload', event => {
        if (event.target?.contains(this.root)) this.unmount();
      });
      this.listen(this.document, 'visibilitychange', () => {
        if (this.document.hidden) this.stop();
      });
      this.listen(window, 'pagehide', () => this.stop());
      if (window.MutationObserver && this.document.documentElement) {
        this.observer = new window.MutationObserver(() => {
          if (!this.root.isConnected) this.unmount();
        });
        this.observer.observe(this.document.documentElement, { childList: true, subtree: true });
      }
    }

    init() {
      const leftContainer = this.root.querySelector('.examples-left');
      const rightContainer = this.root.querySelector('.examples-right');
      this.tracks.forEach((track, index) => {
        const container = index % 2 === 0 ? leftContainer : rightContainer;
        this.createTrackElement(track, index, container);
      });
    }

    createTrackElement(track, index, container) {
      const loader = this.document.createElement('div');
      loader.className = 'loader';

      const songInfo = this.document.createElement('div');
      songInfo.className = 'song';

      const nameP = this.document.createElement('p');
      nameP.className = 'name';
      nameP.textContent = track.title;

      songInfo.appendChild(nameP);

      const playButton = this.document.createElement('div');
      playButton.className = 'play';

      loader.appendChild(songInfo);
      loader.appendChild(playButton);

      const audio = this.document.createElement('audio');
      audio.src = track.url;

      this.listen(audio, 'ended', () => {
        if (audio.ended && this.currentlyPlaying === index) this.stop();
      });

      this.audioElements.set(index, {
        audio,
        loader,
        playButton
      });

      this.listen(loader, 'click', () => this.togglePlay(index));
      container.appendChild(loader);
    }

    ownsPlayback(token) {
      return !this.destroyed && this.root.isConnected !== false && token !== null && this.playToken === token && playback.isCurrent(this, token);
    }

    async togglePlay(index) {
      const track = this.audioElements.get(index);
      if (!track || this.destroyed || this.root.isConnected === false) return false;

      if (this.currentlyPlaying === index && (this.pendingIndex === index || !track.audio.paused)) {
        this.stop();
        return false;
      }

      this.stop();
      const token = playback.claim(this, () => this.stop());
      this.playToken = token;
      this.currentlyPlaying = index;
      this.pendingIndex = index;
      track.playToken = token;
      this.updateControlsToPlaying(track);
      try {
        await track.audio.play();
        if (!this.ownsPlayback(token)) {
          if (track.playToken === token) {
            track.audio.pause();
            track.audio.currentTime = 0;
          }
          return false;
        }
        this.pendingIndex = null;
        return true;
      } catch (error) {
        if (this.ownsPlayback(token)) {
          this.stop();
          console.warn('[MixWave audio] Playback could not start.', error.message);
        }
        return false;
      }
    }

    stop() {
      playback.release(this, this.playToken);
      this.playToken = null;
      if (this.currentlyPlaying !== null) {
        const track = this.audioElements.get(this.currentlyPlaying);
        track.audio.pause();
        track.audio.currentTime = 0;
        this.updateControlsToPlay(track);
      }
      this.currentlyPlaying = null;
      this.pendingIndex = null;
    }

    destroy() {
      if (this.destroyed) return;
      this.stop();
      this.destroyed = true;
      this.listeners.forEach(remove => remove());
      this.listeners = [];
      this.observer?.disconnect();
      this.audioElements.forEach(track => track.loader?.remove());
      instances.delete(this.root);
    }

    unmount() {
      window.MWStorefrontAudio?.unmount?.(this.mountRoot);
      this.destroy();
    }

    updateControlsToPlaying(track) {
      if (track.playButton) {
        track.playButton.remove();
      }

      const loading = this.document.createElement('div');
      loading.className = 'loading';

      for (let i = 0; i < 4; i++) {
        const loadBar = this.document.createElement('div');
        loadBar.className = 'load';
        loading.appendChild(loadBar);
      }

      track.loader.appendChild(loading);
      track.playButton = loading;
    }

    updateControlsToPlay(track) {
      if (track.playButton) {
        track.playButton.remove();
      }

      const playButton = this.document.createElement('div');
      playButton.className = 'play';
      track.loader.appendChild(playButton);
      track.playButton = playButton;
    }
  }

  window.MWAudioPlayer = AudioPlayer;
})();
